import { CertificationsList } from '@/components/Certifications';
import { COMMON_TEXT } from '@/constants';
import { getUserRole } from '@/utils';
import { SlideoutModalCertificate, SlideoutModalSimulationScore } from '@/components/Modals';
import { TextContent } from '@/components/Text';
import { useSalesRepDashboardFeature } from '@/features/sales-rep-dashboard/useSalesRepDashboardFeature';
import { UserProfileCard } from '@/components/Users';
import { ICertification, IUserBadge } from '@/types';
import React from 'react';

/** Props for the DashboardHeader component */
interface DashboardHeaderProps {
  firstName: string;
  isLoading: boolean;
}

/** Props for the DashboardContent component */
interface DashboardContentProps {
  badges: IUserBadge[];
  certifications: ICertification[];
  certificationsError: { message: string; status: number } | null;
  imageUrl?: string;
  isLoading: boolean;
  name: string;
  onViewScoreCard: (scoreId: string) => void;
  roleTitle: string;
  userId?: string;
}

/** Props for the DashboardModals component */
interface DashboardModalsProps {
  context: 'Sales Representative' | 'Manager';
}

/**
 * DashboardHeader Component
 *
 * This component renders the header of the SalesRepDashboard, displaying a welcome message
 * with the user's first name. It also handles the loading state for the header content.
 */
const DashboardHeader: React.FC<DashboardHeaderProps> = ({ firstName, isLoading }) => (
  <div className="mb-10">
    <TextContent
      isLoading={isLoading}
      label={`${COMMON_TEXT.WELCOME}, ${firstName} 👋`}
      level={1}
      loadingClassName="w-1/3"
      testId="sales-rep-dashboard-welcome-header"
      type="headline"
    />
  </div>
);

/**
 * DashboardContent Component
 *
 * This component renders the main content of the SalesRepDashboard, including the certifications
 * list and the user profile. It receives all necessary data and handlers as props to ensure
 * separation of concerns.
 */
const DashboardContent: React.FC<DashboardContentProps> = ({
  badges,
  certifications,
  certificationsError,
  imageUrl,
  isLoading,
  name,
  onViewScoreCard,
  roleTitle,
  userId,
}) => (
  <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
    {/* Left Column - Certifications */}
    <div className="order-last lg:order-first lg:col-span-7 xl:col-span-8 2xl:col-span-9">
      <CertificationsList
        certifications={certifications}
        error={certificationsError}
        isLoading={isLoading}
        onViewScoreCard={onViewScoreCard}
        userId={userId}
      />
    </div>

    {/* Right Column - Profile */}
    <div className="lg:col-span-5 xl:col-span-4 2xl:col-span-3">
      <UserProfileCard
        badges={badges}
        imageUrl={imageUrl}
        isLoading={isLoading}
        name={name}
        title={roleTitle}
      />
    </div>
  </div>
);

/**
 * DashboardModals Component
 *
 * This component renders the modals used in the SalesRepDashboard. It receives a context prop
 * to determine which modals to display and what content to show within them.
 */
const DashboardModals: React.FC<DashboardModalsProps> = ({ context }) => (
  <>
    <SlideoutModalCertificate context={context} />
    <SlideoutModalSimulationScore context={context} />
  </>
);

/**
 * SalesRepDashboard Component
 *
 * This component serves as the main dashboard for sales representatives, providing an
 * overview of their certifications, simulations, and performance scores. It also includes
 * a user profile section and a sidebar for viewing detailed scorecards.
 */
export const SalesRepDashboard: React.FC = () => {
  // =============================================================
  // Feature Hook
  // =============================================================
  const {
    certifications,
    certificationsError,
    currentUser,
    currentUserBadges,
    currentUserFirstName,
    currentUserFullName,
    handleViewScoreCardClick,
    isLoading,
  } = useSalesRepDashboardFeature();

  // ================================
  // Derived values
  // ================================
  const dashboardContext = 'Sales Representative';
  const roleTitle = currentUser?.role ? (getUserRole(currentUser.role) as string) : '';

  // =============================================================
  // Render
  // =============================================================
  return (
    <>
      {/* Dashboard Header */}
      <DashboardHeader firstName={currentUserFirstName} isLoading={isLoading} />

      {/* Dashboard Content */}
      <DashboardContent
        badges={currentUserBadges}
        certifications={certifications}
        certificationsError={certificationsError}
        imageUrl={currentUser?.imageUrl}
        isLoading={isLoading}
        name={currentUserFullName}
        onViewScoreCard={handleViewScoreCardClick}
        roleTitle={roleTitle}
        userId={currentUser?.uuid}
      />

      {/* Dashboard Modals */}
      <DashboardModals context={dashboardContext} />
    </>
  );
};

SalesRepDashboard.displayName = 'SalesRepDashboard';

export default SalesRepDashboard;
